<template>
    <div class="intake-wizard">
        <div v-if="isSubmitted" class="intake-submitted">
            <h2 class="intake-submitted__heading">{{ $t('pages.intake.success.heading') }}</h2>
            <p class="intake-submitted__message">{{ $t('pages.intake.success.message') }}</p>

            <div class="intake-submitted__actions">
                <common-button
                    class="intake-submitted__action"
                    :label="copyButtonLabel"
                    type="primary"
                    size="large"
                    :isSubmittable="false"
                    @click="copySummaryToClipboard"
                ></common-button>
                <common-button
                    class="intake-submitted__action"
                    :label="$t('pages.intake.success.download')"
                    type="secondary"
                    size="large"
                    :isSubmittable="false"
                    @click="downloadSummaryPdf"
                ></common-button>
            </div>

            <div class="intake-submitted__summary-wrap">
                <pre class="intake-submitted__summary">{{ submittedSummaryText }}</pre>
            </div>

            <div class="intake-submitted__footer">
                <common-button
                    class="intake-submitted__close"
                    :label="$t('common.microcopy.close')"
                    type="secondary"
                    size="large"
                    :isSubmittable="false"
                    @click="closeSubmitted"
                ></common-button>
            </div>
        </div>

        <div v-else>
            <common-content-spacer height="small">
                <common-progress
                    :percentage="stepProgress"
                    :showSuccessIcon="false"
                    height="small"
                    color="secondary"
                ></common-progress>
                <p class="intake-page__step-label">{{ currentStepMeta.progress }}</p>
            </common-content-spacer>

            <div class="intake-page__step-intro">
                <common-page-header class="intake-page__step-heading" :heading="currentStepMeta.heading"></common-page-header>
                <p v-if="currentStepMeta.description" class="intake-page__step-description">
                    {{ currentStepMeta.description }}
                </p>
            </div>

            <component
                :is="currentStepComponent"
                :key="`${type}-${currentStep}`"
                v-bind="currentStepBindings"
                @sent="onCurrentStepSent"
                @back="onStepBack"
                @edit="onReviewEdit"
            ></component>

            <p v-if="submitError" class="intake-page__submit-error">{{ submitError }}</p>
        </div>
    </div>
</template>

<script>
    import AccidentIntakeAboutYouForm from 'components/forms/AccidentIntakeAboutYouForm'
    import AccidentIntakeWhatHappenedForm from 'components/forms/AccidentIntakeWhatHappenedForm'
    import AccidentIntakeHowYouFeltForm from 'components/forms/AccidentIntakeHowYouFeltForm'
    import AccidentIntakeSymptomsTodayForm from 'components/forms/AccidentIntakeSymptomsTodayForm'
    import AccidentIntakeSymptomDetailsForm from 'components/forms/AccidentIntakeSymptomDetailsForm'
    import AccidentIntakeHealthScreeningForm from 'components/forms/AccidentIntakeHealthScreeningForm'
    import AccidentIntakeTreatmentReceivedForm from 'components/forms/AccidentIntakeTreatmentReceivedForm'
    import AccidentIntakeTreatingProvidersForm from 'components/forms/AccidentIntakeTreatingProvidersForm'
    import AccidentIntakeTestsImagingForm from 'components/forms/AccidentIntakeTestsImagingForm'
    import AccidentIntakePreviousInjuriesForm from 'components/forms/AccidentIntakePreviousInjuriesForm'
    import AccidentIntakeMedicalSurgicalHistoryForm from 'components/forms/AccidentIntakeMedicalSurgicalHistoryForm'
    import AccidentIntakeMedicationsAllergiesForm from 'components/forms/AccidentIntakeMedicationsAllergiesForm'
    import AccidentIntakeWorkInformationForm from 'components/forms/AccidentIntakeWorkInformationForm'
    import AccidentIntakeSocialFamilyHistoryForm from 'components/forms/AccidentIntakeSocialFamilyHistoryForm'
    import AccidentIntakeDailyActivitiesForm from 'components/forms/AccidentIntakeDailyActivitiesForm'
    import AccidentIntakeHealthQuestionsForm from 'components/forms/AccidentIntakeHealthQuestionsForm'
    import AccidentIntakeReviewSubmitForm from 'components/forms/AccidentIntakeReviewSubmitForm'
    import AccidentIntakeConfirmSubmitForm from 'components/forms/AccidentIntakeConfirmSubmitForm'
    import GeneralIntakePreventiveCareForm from 'components/forms/GeneralIntakePreventiveCareForm'
    import IntakeAPI from 'api/IntakeAPI'
    import { buildIntakePayload } from '~/helpers/intakePayload'
    import { createEmptyFormData, getIntakeWizardConfig } from '~/helpers/intakeWizardConfig'

    const STEP_FORMS = {
        aboutYou: AccidentIntakeAboutYouForm,
        whatHappened: AccidentIntakeWhatHappenedForm,
        howYouFelt: AccidentIntakeHowYouFeltForm,
        symptomsToday: AccidentIntakeSymptomsTodayForm,
        symptomDetails: AccidentIntakeSymptomDetailsForm,
        healthScreening: AccidentIntakeHealthScreeningForm,
        treatmentReceived: AccidentIntakeTreatmentReceivedForm,
        treatingProviders: AccidentIntakeTreatingProvidersForm,
        testsImaging: AccidentIntakeTestsImagingForm,
        previousInjuries: AccidentIntakePreviousInjuriesForm,
        medicalSurgical: AccidentIntakeMedicalSurgicalHistoryForm,
        medicationsAllergies: AccidentIntakeMedicationsAllergiesForm,
        workInformation: AccidentIntakeWorkInformationForm,
        socialFamily: AccidentIntakeSocialFamilyHistoryForm,
        dailyActivities: AccidentIntakeDailyActivitiesForm,
        preventiveCare: GeneralIntakePreventiveCareForm,
        healthQuestions: AccidentIntakeHealthQuestionsForm,
        reviewSubmit: AccidentIntakeReviewSubmitForm,
        confirmSubmit: AccidentIntakeConfirmSubmitForm,
    }

    export default {
        props: {
            type: {
                type: String,
                default: 'accident',
                validator: (value) => ['accident', 'general'].includes(value),
            },
            patientDetails: {
                type: Object,
                default: null,
            },
            patientId: {
                type: [String, Number],
                default: null,
            },
            authenticated: {
                type: Boolean,
                default: false,
            },
        },

        data() {
            return {
                currentStep: 1,
                returnToStep: null,
                isSubmitting: false,
                isSubmitted: false,
                submitError: null,
                submittedSummaryText: '',
                submittedPdfFile: null,
                isSummaryCopied: false,
                formData: createEmptyFormData(getIntakeWizardConfig(this.type).steps, this.buildAboutYouPrefill()),
            }
        },

        computed: {
            wizardConfig() {
                return getIntakeWizardConfig(this.type)
            },

            totalSteps() {
                return this.wizardConfig.steps.length
            },

            visibleStepCount() {
                return this.wizardConfig.steps.filter((_, index) => !this.shouldSkipStep(index + 1)).length
            },

            displayStepNumber() {
                const skippedBefore = this.wizardConfig.steps
                    .slice(0, this.currentStep - 1)
                    .filter((_, index) => this.shouldSkipStep(index + 1)).length

                return this.currentStep - skippedBefore
            },

            reviewStep() {
                const index = this.wizardConfig.steps.findIndex((step) => step.isReview)
                return index + 1
            },

            currentStepConfig() {
                return this.wizardConfig.steps[this.currentStep - 1] || this.wizardConfig.steps[0]
            },

            currentStepComponent() {
                return STEP_FORMS[this.currentStepConfig.key]
            },

            currentStepBindings() {
                const step = this.currentStepConfig
                const dataKey = step.dataKey || step.key
                const bindings = {
                    initialData: this.formData[dataKey],
                }

                if (step.isReview) {
                    bindings.intakeData = this.formData
                    if (step.reviewVariant) {
                        bindings.variant = step.reviewVariant
                    }
                }

                if (step.isConfirm) {
                    bindings.isSubmitting = this.isSubmitting
                    bindings.stackedActions = this.type === 'general'
                }

                return bindings
            },

            currentStepMeta() {
                const prefix = this.wizardConfig.i18nPrefix
                const stepKey = `${prefix}.step${this.currentStep}`

                return {
                    progress: this.$t(`${stepKey}.progress`, {
                        current: this.displayStepNumber,
                        total: this.visibleStepCount,
                    }),
                    heading: this.$t(`${stepKey}.heading`),
                    description: this.$t(`${stepKey}.description`),
                }
            },

            stepProgress() {
                return Math.round((this.displayStepNumber / this.visibleStepCount) * 100)
            },

            copyButtonLabel() {
                return this.isSummaryCopied ? this.$t('pages.intake.success.copied') : this.$t('pages.intake.success.copy')
            },
        },

        methods: {
            buildAboutYouPrefill() {
                const patient = this.patientDetails
                if (!patient || !Object.keys(patient).length) {
                    return undefined
                }

                const fullName = [patient.firstName, patient.lastName].filter(Boolean).join(' ').trim()

                return {
                    informationProvider: 'patient',
                    fullName: fullName || undefined,
                    dateOfBirth: patient.dateOfBirth || patient.dob || undefined,
                    phone: patient.mobilePhone || patient.phone || undefined,
                    email: patient.email || undefined,
                }
            },

            onCurrentStepSent(data) {
                if (this.currentStepConfig.isConfirm) {
                    this.onFinalSubmit(data)
                    return
                }

                this.onStepComplete(data)
            },

            stepDataKey(step = this.currentStepConfig) {
                return step.dataKey || step.key
            },

            hasComplaints() {
                return (this.formData.symptomsToday?.problems || []).length > 0
            },

            shouldSkipStep(stepNumber) {
                const step = this.wizardConfig.steps[stepNumber - 1]
                return Boolean(step?.skipUnlessComplaints && !this.hasComplaints())
            },

            goToAdjacentStep(direction) {
                let next = this.currentStep + direction
                while (next >= 1 && next <= this.totalSteps && this.shouldSkipStep(next)) {
                    next += direction
                }

                if (next >= 1 && next <= this.totalSteps) {
                    this.currentStep = next
                }
            },

            saveStepData(data) {
                const key = this.stepDataKey()
                if (!key) {
                    return
                }

                if (key === 'symptomsToday' && this.formData.symptomsToday) {
                    this.formData[key] = {
                        ...this.formData.symptomsToday,
                        ...data,
                    }
                    return
                }

                this.formData[key] = data
            },

            onStepComplete(data) {
                this.saveStepData(data)

                if (this.returnToStep) {
                    this.currentStep = this.returnToStep
                    this.returnToStep = null
                    return
                }

                this.goToAdjacentStep(1)
            },

            onStepBack(data) {
                this.saveStepData(data)

                if (this.returnToStep) {
                    this.currentStep = this.returnToStep
                    this.returnToStep = null
                    return
                }

                this.goToAdjacentStep(-1)
            },

            onReviewEdit({ step, data }) {
                this.formData.reviewSubmit = data
                this.returnToStep = this.reviewStep
                this.currentStep = step
            },

            async onFinalSubmit(data) {
                this.formData.confirmSubmit = data
                this.isSubmitting = true
                this.isSubmitted = false
                this.submitError = null

                const patientId = Number(this.patientId || this.$route.params.id)
                const userId = Number(this.$auth.getUser().id)
                const fromPracticeId = Number(
                    this.$auth.getUser().practiceId || (this.$auth.getPractice() && this.$auth.getPractice().id),
                )
                const payload = buildIntakePayload(this.formData, {
                    patientId,
                    authenticated: true,
                    type: this.type,
                })

                try {
                    if (!patientId || !userId || Number.isNaN(patientId) || Number.isNaN(userId)) {
                        throw new Error(this.$t('pages.intake.submit_error'))
                    }
                    if (!fromPracticeId || Number.isNaN(fromPracticeId)) {
                        throw new Error(this.$t('pages.intake.submit_error'))
                    }

                    const result = await IntakeAPI.submitAndUpload({
                        patientId,
                        userId,
                        fromPracticeId,
                        payload,
                    })

                    this.submittedSummaryText = result.summaryText || ''
                    this.submittedPdfFile = result.pdfFile || null
                    this.isSummaryCopied = false
                    this.isSubmitted = true
                    window.scrollTo({ top: 0, behavior: 'smooth' })
                } catch (error) {
                    const responseMessage = Array.isArray(error?.response?.data)
                        ? error.response.data[0]?.message
                        : error?.response?.data?.message
                    this.submitError = responseMessage || error?.message || this.$t('pages.intake.submit_error')
                } finally {
                    this.isSubmitting = false
                }
            },

            async copySummaryToClipboard() {
                const text = this.submittedSummaryText
                if (!text) {
                    return
                }

                try {
                    if (navigator.clipboard && navigator.clipboard.writeText) {
                        await navigator.clipboard.writeText(text)
                    } else {
                        const textarea = document.createElement('textarea')
                        textarea.value = text
                        textarea.setAttribute('readonly', '')
                        textarea.style.position = 'absolute'
                        textarea.style.left = '-9999px'
                        document.body.appendChild(textarea)
                        textarea.select()
                        document.execCommand('copy')
                        document.body.removeChild(textarea)
                    }
                    this.isSummaryCopied = true
                    window.setTimeout(() => {
                        this.isSummaryCopied = false
                    }, 2000)
                } catch (error) {
                    this.submitError = error?.message || this.$t('pages.intake.submit_error')
                }
            },

            downloadSummaryPdf() {
                const pdfFile = this.submittedPdfFile
                if (!pdfFile?.file) {
                    return
                }

                const link = document.createElement('a')
                link.href = pdfFile.file
                link.download = pdfFile.fileName || 'accident_patient_intake.pdf'
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
            },

            closeSubmitted() {
                this.$emit('close')
            },
        },

        watch: {
            currentStep() {
                window.scrollTo({ top: 0, behavior: 'smooth' })
            },
            patientDetails: {
                deep: true,
                handler(patient) {
                    if (this.currentStep !== 1 || this.formData.aboutYou) {
                        return
                    }
                    if (patient && Object.keys(patient).length) {
                        this.formData.aboutYou = this.buildAboutYouPrefill()
                    }
                },
            },
        },
    }
</script>

<style scoped lang="scss">
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/_functions.scss';

    .intake-wizard {
        width: 100%;
        display: flex;
        flex-direction: column;
        margin-bottom: 20px;
        padding: 28px;
        border-radius: 12px;
        border: 1px solid #ddd;
        background-color: #fff;
    }

    .intake-page__step-label {
        margin: 0;
        font-size: rem($font-size--form);
        color: $color--txt-minor;
    }

    .intake-page__step-intro {
        margin: rem(20px) 0 rem(24px);
    }

    .intake-page__step-heading {
        ::v-deep .page-header__heading {
            margin: 0;
            line-height: 1.2;
        }
    }

    .intake-page__step-description {
        margin: rem(6px) 0 0;
        font-size: rem($font-size--hint);
        line-height: 1.4;
        color: $color--txt-base;
    }

    .intake-page__submit-error {
        margin: rem(16px) 0 0;
        font-size: rem($font-size--form);
        color: $color--accent-4;
    }

    .intake-submitted {
        display: flex;
        flex-direction: column;
        align-items: stretch;
        width: 100%;
    }

    .intake-submitted__heading {
        margin: 0;
        text-align: center;
        font-size: rem($font-size--heading);
        font-weight: 700;
        color: $color--txt-header;
    }

    .intake-submitted__message {
        margin: rem(12px) auto rem(24px);
        max-width: rem(640px);
        text-align: center;
        font-size: rem($font-size--form);
        line-height: 1.5;
        color: $color--txt-base;
    }

    .intake-submitted__actions {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        justify-content: center;
        align-items: center;
        width: 100%;
        margin: 0 0 rem(20px);
        gap: rem(16px);
    }

    .intake-submitted__actions ::v-deep .button {
        flex: 0 0 auto;
        width: auto;
        margin: 0;
    }

    .intake-submitted__action {
        flex: 0 0 auto;
        margin: 0 !important;
    }

    .intake-submitted__summary-wrap {
        width: 100%;
        max-height: rem(420px);
        overflow: hidden;
        border: rem(1px) solid $color--border-base;
        border-radius: rem($border-radius--base);
        background: #fff;
        box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
    }

    .intake-submitted__summary {
        margin: 0;
        padding: rem(20px);
        max-height: rem(420px);
        overflow-x: hidden;
        overflow-y: auto;
        text-align: left;
        white-space: pre-wrap;
        word-break: break-word;
        font-family: Consolas, 'Courier New', monospace;
        font-size: rem(13px);
        line-height: 1.55;
        color: $color--txt-base;
        background: transparent;
        border: 0;
        box-sizing: border-box;
    }

    .intake-submitted__footer {
        display: flex;
        justify-content: center;
        width: 100%;
        margin: rem(20px) 0 0;
    }

    .intake-submitted__close {
        flex: 0 0 auto;
        margin: 0 !important;
    }
</style>
